import { computed, inject } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
  withHooks,
} from '@ngrx/signals';
import { IndicatorStore } from './indicator/indicator.state';
import { DataLayerTypeStore } from './data-layer-type/data-layer-type.state';
import { BookmarkStore } from './bookmark/bookmark.state';
import { StoryStore } from './story/story.state';
import { ColorsetStore } from './colorset/colorset.state';
import { TopicStore } from './topic/topic.state';
import { makeUniqueStringArray } from '../utility/array.utility';
import { GeoDataStore } from './geo/geo-data.state';

type GlobalSearchState = {
  query: string;
  tags: string[];
  tagsInUse: string[];
  results: any[];
  addChildren: boolean;
};

const initialState: GlobalSearchState = {
  query: '',
  tags: [],
  tagsInUse: [],
  results: [],
  addChildren: false,
};

export const GlobalSearchStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed(
    (
      store,
      indicatorStore = inject(IndicatorStore),
      layerTypeStore = inject(DataLayerTypeStore),
      bookmarkStore = inject(BookmarkStore),
      storyStore = inject(StoryStore),
      colorsetStore = inject(ColorsetStore),
      topicStore = inject(TopicStore),
      geoStore = inject(GeoDataStore)
    ) => ({
      filteredResults: computed(() => {
        const searchQuery = store.query().toLowerCase();
        const tags = store.tags();
        const indicatorResults = indicatorStore.globalSearch(
          searchQuery,
          store.addChildren(),
          tags
        );
        const isEmptySearch =
          searchQuery.trim().length === 0 &&
          tags.length === 0 &&
          indicatorStore.filter().category.length === 0;

        const layerTypeResults = layerTypeStore.globalSearch(
          searchQuery,
          tags,
          'Polygon'
        );
        const poiTypeResults = layerTypeStore.globalSearch(
          searchQuery,
          tags,
          'Point'
        );
        const bookmarkResults = bookmarkStore.globalSearch(searchQuery, tags);
        const storyResults = storyStore.globalSearch(searchQuery, tags);
        const topicResults =
          tags && tags.length ? [] : topicStore.globalSearch(searchQuery, tags);
        const colorsetResults =
          tags && tags.length ? [] : colorsetStore.globalSearch(searchQuery);
        const districtResults =
          tags && tags.length ? [] : geoStore.globalSearch(searchQuery, false);
        const poiResults =
          tags && tags.length ? [] : geoStore.globalSearch(searchQuery, true);
        const allResults = isEmptySearch
          ? []
          : [
              ...indicatorResults,
              ...layerTypeResults,
              ...poiTypeResults,
              ...bookmarkResults,
              ...storyResults,
              ...colorsetResults,
              ...topicResults,
              ...districtResults,
              ...poiResults,
            ];

        const combinedResults = {
          indicators: indicatorResults,
          dataLayerTypes: layerTypeResults,
          poiLayerTypes: poiTypeResults,
          bookmarks: bookmarkResults,
          stories: storyResults,
          colorsets: colorsetResults,
          topics: topicResults,
          districts: districtResults,
          pois: poiResults,
          all: allResults,
        };
        return combinedResults;
      }),
      tagsInUse: computed(() => {
        return makeUniqueStringArray([
          ...indicatorStore.tagsInUse(),
          ...layerTypeStore.tagsInUse(),
          ...bookmarkStore.tagsInUse(),
          ...storyStore.tagsInUse(),
          ...topicStore.tagsInUse(),
        ]);
      }),
    })
  ),
  withMethods(
    (
      store,
      indicatorStore = inject(IndicatorStore),
      layerTypeStore = inject(DataLayerTypeStore),
      bookmarkStore = inject(BookmarkStore),
      storyStore = inject(StoryStore),
      colorsetStore = inject(ColorsetStore),
      topicStore = inject(TopicStore),
      geoStore = inject(GeoDataStore)
    ) => ({
      updateQuery(newQuery: string, tags?: string[], addChildren = false) {
        const updates: Partial<GlobalSearchState> = { query: newQuery };
        if (addChildren) updates.addChildren = true;
        if (tags) updates.tags = tags;
        patchState(store, updates);
      },
      clearQuery() {
        patchState(store, { query: '' });
      },
      isType(type: string, id: string) {
        switch (type) {
          case 'indicator':
            return !!indicatorStore.items()[id];
          case 'dataLayerType':
            // eslint-disable-next-line no-case-declarations
            const layerType = layerTypeStore.items()[id];
            return !!layerType && layerType.type === 'Polygon';
          case 'poiLayerType':
            // eslint-disable-next-line no-case-declarations
            const poiLayerType = layerTypeStore.items()[id];
            return !!poiLayerType && poiLayerType.type === 'Point';
          case 'bookmark':
            return !!bookmarkStore.items()[id];
          case 'story':
            return !!storyStore.items()[id];
          case 'topic':
            return !!topicStore.items()[id];
          case 'district':
            // eslint-disable-next-line no-case-declarations
            const districtGeoData = geoStore.items()[id];
            return !!districtGeoData && !districtGeoData.poiJson;
          case 'poi':
            // eslint-disable-next-line no-case-declarations
            const poiGeoData = geoStore.items()[id];
            return !!poiGeoData && poiGeoData.poiJson;
        }
        return undefined;
      },
    })
  ),
  withHooks({
    onInit(store) {},
    onDestroy() {
      console.log('on destroy');
    },
  })
);
